import React, { useRef, useState } from 'react';
import { Button } from '../Button';
import { Box } from '../Layout';
import { Text } from '../Typography';
import { AdaptiveMasonryLayout, type MediaItem } from '../AdaptiveMasonryLayout';

export interface MediaFile {
  id: string;
  file: File;
  url: string;
  type: 'image' | 'video';
  width?: number;
  height?: number;
  altText?: string;
}

export interface MediaUploaderProps {
  /** Maximum number of files */
  maxFiles?: number;
  /** Maximum file size in bytes */
  maxFileSize?: number;
  /** Accepted file types */
  acceptedTypes?: string[];
  /** Current uploaded files */
  files?: MediaFile[];
  /** Callback when files are added */
  onFilesAdd?: (files: MediaFile[]) => void;
  /** Callback when file is removed */
  onFileRemove?: (fileId: string) => void;
  /** Callback when alt text is updated */
  onAltTextUpdate?: (fileId: string, altText: string) => void;
  /** Custom styling */
  className?: string;
  /** Upload button text */
  uploadButtonText?: string;
  /** Show preview thumbnails */
  showPreviews?: boolean;
}

export const MediaUploader: React.FC<MediaUploaderProps> = ({
  maxFiles = 4,
  maxFileSize = 5 * 1024 * 1024, // 5MB
  acceptedTypes = ['image/*', 'video/*'],
  files = [],
  onFilesAdd,
  onFileRemove,
  onAltTextUpdate,
  className = '',
  uploadButtonText = '📷 Add Media',
  showPreviews = true,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = async (selectedFiles: FileList | null) => {
    if (!selectedFiles || !onFilesAdd) return;

    const newFiles: MediaFile[] = [];
    const remainingSlots = maxFiles - files.length;

    for (let i = 0; i < Math.min(selectedFiles.length, remainingSlots); i++) {
      const file = selectedFiles[i];

      // Skip if file is undefined
      if (!file) continue;

      // Check file size
      if (file.size > maxFileSize) {
        console.warn(`File ${file.name} is too large (${Math.round(file.size / 1024 / 1024)}MB)`);
        continue;
      }

      // Determine file type
      const type = file.type.startsWith('image/') ? 'image' : 'video';
      const url = URL.createObjectURL(file);

      // Get dimensions for images
      let width: number | undefined;
      let height: number | undefined;

      if (type === 'image') {
        try {
          const dimensions = await getImageDimensions(url);
          width = dimensions.width;
          height = dimensions.height;
        } catch (error) {
          console.warn('Failed to get image dimensions:', error);
        }
      }

      const mediaFile: MediaFile = {
        id: `${Date.now()}-${i}`,
        file,
        url,
        type,
        ...(width !== undefined && { width }),
        ...(height !== undefined && { height }),
        altText: '',
      };

      newFiles.push(mediaFile);
    }

    if (newFiles.length > 0) {
      onFilesAdd(newFiles);
    }
  };

  // Helper function to get image dimensions
  const getImageDimensions = (url: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const canAddMore = files.length < maxFiles;

  return (
    <Box className={`space-y-2 ${className}`}>
      {/* Upload Area */}
      {canAddMore && (
        <Box
          className={`border-2 border-dashed rounded-lg p-4 transition-colors ${
            dragOver ? 'border-primary bg-primary/10' : 'border-base-300 hover:border-primary/50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Box className="text-center space-y-2">
            <Button variant="ghost" onClick={handleButtonClick} className="w-full">
              {uploadButtonText}
            </Button>
            <Text size="xs" className="text-base-content/60">
              Drag and drop files here, or click to select
            </Text>
            <Text size="xs" className="text-base-content/40">
              Max {maxFiles} files, {Math.round(maxFileSize / 1024 / 1024)}MB each
            </Text>
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={acceptedTypes.join(',')}
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </Box>
      )}

      {/* File Previews */}
      {showPreviews && files.length > 0 && (
        <AdaptiveMasonryLayout
          items={files.map((file): MediaItem => ({
            id: file.id,
            url: file.url,
            ...(file.width !== undefined && { width: file.width }),
            ...(file.height !== undefined && { height: file.height }),
            type: file.type,
            ...(file.altText !== undefined && { altText: file.altText }),
          }))}
          className="media-uploader-previews"
          onItemClick={(item) => {
            // Handle click if needed - could open edit modal
            console.log('Edit media item:', item);
          }}
        />
      )}

      {/* Individual Controls */}
      {showPreviews && files.length > 0 && (
        <Box className="space-y-2 mt-2">
          {files.map((file) => (
            <Box key={file.id} className="flex items-center gap-2 p-2 bg-base-200 rounded">
              <Text size="xs" className="flex-1 truncate">
                {file.file.name}
              </Text>
              
              {file.type === 'image' && (
                <input
                  type="text"
                  placeholder="Alt text..."
                  value={file.altText || ''}
                  onChange={(e) => onAltTextUpdate?.(file.id, e.target.value)}
                  className="text-xs px-2 py-1 rounded border border-base-300 bg-base-100 w-24"
                />
              )}
              
              <Button
                variant="error"
                size="xs"
                onClick={() => onFileRemove?.(file.id)}
                className="rounded-full p-1"
              >
                ✕
              </Button>
            </Box>
          ))}
        </Box>
      )}

      {/* File Count */}
      {files.length > 0 && (
        <Text size="xs" className="text-base-content/60">
          {files.length}/{maxFiles} files uploaded
        </Text>
      )}
    </Box>
  );
};


export default MediaUploader;
